import { Client, Warehouse, WarehouseStockItem, NipBasicData } from '../../features/pos/models/api.model';
import { CommitSaleRequest, CommitSaleResult } from '../../features/pos/services/commit-sale.service';
import { FiscalReportSummary } from '../../features/pos/services/fiscal-report.service';

let saleSeq = 4100;
let receiptSeq = 8200;

function rid(prefix: string): string {
  return `${prefix}-${Math.random().toString(36).slice(2, 10)}`;
}

function nowUnix(): number {
  return Math.floor(Date.now() / 1000);
}

export function mockKeycloakToken(): unknown {
  return {
    access_token: 'mock.access.' + Math.random().toString(36).slice(2),
    refresh_token: 'mock.refresh.' + Math.random().toString(36).slice(2),
    expires_in: 8 * 3600,
    refresh_expires_in: 12 * 3600,
    token_type: 'Bearer',
    scope: 'openid',
  };
}

const WAREHOUSE: Warehouse = { id: 'wh-main', name: 'Magazyn główny', isDefault: true };

const STOCK: WarehouseStockItem[] = [
  { id: 'st-1', warehouseId: WAREHOUSE.id, productId: 'p-1', productName: 'Kawa espresso', unit: 'szt', quantity: 999, avgNetPrice: 8.13, vatLabel: '23' },
  { id: 'st-2', warehouseId: WAREHOUSE.id, productId: 'p-2', productName: 'Herbata', unit: 'szt', quantity: 500, avgNetPrice: 6.5, vatLabel: '23' },
  { id: 'st-3', warehouseId: WAREHOUSE.id, productId: 'p-3', productName: 'Woda mineralna 0,5l', unit: 'szt', quantity: 240, avgNetPrice: 2.78, vatLabel: '23' },
  { id: 'st-4', warehouseId: WAREHOUSE.id, productId: 'p-4', productName: 'Sok pomarańczowy 0,3l', unit: 'szt', quantity: 120, avgNetPrice: 5.56, vatLabel: '23' },
  { id: 'st-5', warehouseId: WAREHOUSE.id, productId: 'p-5', productName: 'Kanapka z szynką', unit: 'szt', quantity: 40, avgNetPrice: 11.11, vatLabel: '8' },
  { id: 'st-6', warehouseId: WAREHOUSE.id, productId: 'p-6', productName: 'Sałatka grecka', unit: 'szt', quantity: 25, avgNetPrice: 16.67, vatLabel: '8' },
  { id: 'st-7', warehouseId: WAREHOUSE.id, productId: 'p-7', productName: 'Ciastko czekoladowe', unit: 'szt', quantity: 60, avgNetPrice: 7.41, vatLabel: '8' },
  { id: 'st-8', warehouseId: WAREHOUSE.id, productId: 'p-8', productName: 'Chleb razowy', unit: 'szt', quantity: 30, avgNetPrice: 4.63, vatLabel: '5' },
  { id: 'st-9', warehouseId: WAREHOUSE.id, productId: 'p-9', productName: 'Baton energetyczny', unit: 'szt', quantity: 0, avgNetPrice: 4.07, vatLabel: '8' },
];

export function mockWarehouseList(): unknown {
  return { data: [WAREHOUSE], count: 1 };
}

export function mockStockList(): unknown {
  return { data: STOCK, count: STOCK.length };
}

const COMPANIES = [
  { id: 'comp-1', basic_data: { name: 'Moja Firma Sp. z o.o.', nip: '1234563218' } },
  { id: 'comp-2', basic_data: { name: 'Sklep na Rogu', nip: '9876543217' } },
];

export function mockUserCompanies(): unknown {
  return { data: COMPANIES, count: COMPANIES.length };
}

export function mockCompanySettings(id: string): unknown {
  const company = COMPANIES.find((c) => c.id === id) ?? COMPANIES[0];
  return {
    id: company.id,
    basic_data: {
      name: company.basic_data.name,
      nip: company.basic_data.nip,
      address: { street: 'Przykładowa', building: '12', postal_code: '00-001', city: 'Warszawa', country_code: 'PL' },
    },
    seller_status: 1,
    payment_data: [],
  };
}

const CLIENTS: Client[] = [
  {
    id: 'cli-1',
    naturalPerson: false,
    idType: 'nip',
    basicData: { name: 'ACME Sp. z o.o.', nip: '5252445211', address: { city: 'Warszawa', postalCode: '02-676', street: 'Postępu', building: '15', countryCode: 'PL' } },
    buyerJst: 2,
    buyerVatGroup: 2,
  },
  {
    id: 'cli-2',
    naturalPerson: false,
    idType: 'nip',
    basicData: { name: 'Nowak Handel Detaliczny', nip: '7010001234', address: { city: 'Kraków', postalCode: '30-001', street: 'Floriańska', building: '4', countryCode: 'PL' } },
    buyerJst: 2,
    buyerVatGroup: 2,
  },
  {
    id: 'cli-3',
    naturalPerson: true,
    idType: 'none',
    basicData: { name: 'Jan Kowalski', address: { city: 'Gdańsk', postalCode: '80-001', street: 'Długa', building: '7', countryCode: 'PL' } },
    buyerJst: 2,
    buyerVatGroup: 2,
  },
];

export function mockClientList(query: string): unknown {
  const q = query.trim().toLowerCase();
  const data = q
    ? CLIENTS.filter((c) => (c.basicData.name ?? '').toLowerCase().includes(q) || (c.basicData.nip ?? '').includes(q))
    : CLIENTS;
  return { data, count: data.length };
}

export function mockCreateClient(): unknown {
  return { id: rid('cli') };
}

export function mockNipData(nip: string): NipBasicData {
  return {
    name: 'Kontrahent ' + nip.slice(-4),
    nip,
    natural_person: false,
    street: 'Handlowa',
    building: '8',
    postal_code: '61-001',
    city: 'Poznań',
    country_code: 'PL',
    email: 'kontakt@kontrahent.pl',
    phone: '+48 61 000 00 00',
  };
}

export function mockCommitSale(req: CommitSaleRequest): CommitSaleResult {
  const isReceipt = req.document_type === 'receipt';
  const number = isReceipt ? `PAR/${++receiptSeq}/2026` : `FV/${++saleSeq}/2026`;
  return {
    document_type: req.document_type,
    document_id: rid(isReceipt ? 'rcp' : 'inv'),
    number,
    offline: false,
  };
}

export function mockNextNumber(): unknown {
  return { next_number: `PAR/${receiptSeq + 1}/2026` };
}

export function mockReceipt(): unknown {
  return { id: rid('rcp'), number: `PAR/${++receiptSeq}/2026`, status: 'issued' };
}

const FISCAL_REPORTS: FiscalReportSummary[] = [
  { id: 'fr-1', type: 'daily_Z', report_number: 'Z/128', device_id: 'mock', issued_at: nowUnix() - 86400, status: 'sent' },
  { id: 'fr-2', type: 'daily_Z', report_number: 'Z/129', device_id: 'mock', issued_at: nowUnix() - 43200, status: 'sent' },
  { id: 'fr-3', type: 'monthly', report_number: 'M/06-2026', device_id: 'mock', period_from: '2026-06-01', period_to: '2026-06-30', issued_at: nowUnix() - 3600, status: 'sent' },
];

export function mockFiscalReportsList(): unknown {
  return { reports: FISCAL_REPORTS, count: FISCAL_REPORTS.length };
}

export function mockAddFiscalReport(req: { type?: string; report_number?: string; period_from?: string; period_to?: string }): FiscalReportSummary {
  const summary: FiscalReportSummary = {
    id: rid('fr'),
    type: req.type ?? 'daily_Z',
    report_number: req.report_number ?? 'Z/130',
    device_id: 'mock',
    period_from: req.period_from,
    period_to: req.period_to,
    issued_at: nowUnix(),
    status: 'sent',
  };
  FISCAL_REPORTS.unshift(summary);
  return summary;
}

export function mockInvoice(): unknown {
  return { id: rid('inv'), number: `FV/${++saleSeq}/2026` };
}
